import { hydrateCreatureCells } from './creature-cells';
import { adoptBaseDefinitionList } from './base-definition-rows';
import { DEFINITION_FAMILY_SCHEMA } from './definition-family-cells';
import { definitionNameCell, definitionPlCell } from './definition-cells';
import { isBaseDefinitionListSchema, SAVED_BASE_DEFINITION_LIST_SCHEMA } from './types';
import type { BaseDefinitionListResponse } from './types';
// render-table.ts — the "catalog-table" renderer: a sortable, per-column-filterable <table>
// Receives table rows directly, or compiles raw spell/ability definitions and builds their cells
// in the browser. The resulting rows stay in memory for all sort/filter work, without jQuery.
//
// This is the modern replacement for the jQuery tablesorter tables. Two wins over tablesorter:
//   1. No jQuery.
//   2. Icon columns sort/filter correctly. tablesorter sorted on a cell's *text*, so an icon cell
//      (just an <img>) had no sort key. Here every cell carries its real `sort`/`text` value in the
//      JSON, and the icon HTML is only how it's *displayed* — so sorting never touches the pixels.
//
// Markup deliberately reuses tablesorter's theme class names (.tablesorter-blue/-dark,
// .tablesorter-header, .tablesorter-headerAsc/Desc, .tablesorter-filter-row, .tablesorter-filter,
// .filtered, tr.odd/.even) so the existing theme CSS styles it for free — same look as the 64 tables
// still on tablesorter, zero new design.

import type { CatalogListConfig, CatalogTableResponse, TableColumn, TableRow, TableCell, CellSegment, IconSegment } from './types';
import { resolveUrl, results, setStatus, showLoader, hideLoader, initTooltips } from './catalog-list';
import { buildThumbnailNode } from '../thumbnail/core';
import { encounterDisplayCells, type EncounterDisplayInput } from '../janus-engine/port/k5-render/encounter-display';
import { rawListAdopter } from './raw-list-schemas';
import { definitionListDataUrl } from './definition-list-rows';
import { parseText, ftMountParseTextTables } from '../shared/parse-text';
import { wireFilterClearButton } from './filter-clear';
import { buildIncompleteNotice } from './dom-helpers';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import {
    plDistinctPendingIds, plPendingTip, plPriceable, plTotalOf, priceDefinitionsInBrowser, waitForPlHooks,
    type PlState, type PlTotalInput,
} from '../shared/pl-browser-pricing';

interface SortState {
    key: string;
    dir: 'asc' | 'desc';
}

type DisplayDefinitions = (ids: string[]) => Promise<Record<string, Record<string, unknown> | null>>;

/** Only the rune segment changes. Name text/link, search keys and all other cells stay intact. */
export function swapDefinitionRune(row: TableRow, td: HTMLTableCellElement, def: Record<string, unknown>): void {
    const current = row.cells.name?.segments?.[0];
    const next = definitionNameCell(def).segments?.[0];
    if (current?.kind !== 'raw' || next?.kind !== 'raw') return;
    if (current.html !== next.html) {
        const holder = document.createElement('span');
        holder.innerHTML = next.html;
        const mount = holder.querySelector('.ft-rune-mount');
        const old = td.querySelector('.ft-rune-mount');
        if (!mount || !old) return;
        row.cells.name!.segments![0] = next;
        old.replaceWith(mount);
    }
    window.ftMountRuneIcons?.(td);
    initTooltips(td);
    td.dataset.ftRune = 'exact';
    delete td.dataset.ftRuneRefusal;
}

/** Visible v2 rows upgrade off the table-ready path. Real results/refusals are
 * terminal; leaving the queue or missing the core hook does not consume an attempt. */
export function observeDefinitionRunes(table: HTMLTableElement, nameColumn: number): (row: TableRow, tr: HTMLTableRowElement) => void {
    type Target = { id: string; row: TableRow; tr: HTMLTableRowElement; td: HTMLTableCellElement };
    const queued = new Map<string, Target>(), visible = new Map<string, Target>();
    const targets = new Map<Element, Target>();
    const settled = new Map<string, string | undefined>(), inFlight = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | undefined, running = false, stopped = false;
    let hookRetryTimer: ReturnType<typeof setTimeout> | undefined, hookRetries = 0;
    let hook: Promise<DisplayDefinitions | null> | undefined;
    const host = window as unknown as { ftDefinitionsForDisplay?: DisplayDefinitions };
    const disconnect = (): void => {
        observer?.disconnect();
        queued.clear(); visible.clear();
        if (timer !== undefined) clearTimeout(timer);
        if (hookRetryTimer !== undefined) clearTimeout(hookRetryTimer);
        timer = undefined;
        hookRetryTimer = undefined;
    };
    const alive = (): boolean => {
        if (!stopped && document.contains(table)) return true;
        stopped = true; disconnect(); targets.clear();
        return false;
    };
    const attached = (target: Target): boolean => table.contains(target.tr) && !target.tr.hidden && target.tr.style.display !== 'none';
    const onScreen = (target: Target): boolean => {
        const rect = target.tr.getBoundingClientRect();
        return attached(target) && rect.bottom > 0 && rect.top < window.innerHeight && rect.height > 0;
    };
    const prune = (): void => {
        for (const [tr, target] of targets) {
            if (attached(target)) continue;
            queued.delete(target.id); visible.delete(target.id);
            if (!table.contains(tr)) { observer?.unobserve(tr); targets.delete(tr); }
        }
        if (!targets.size) disconnect();
    };
    const waitForHook = async (): Promise<DisplayDefinitions | null> => {
        const deadline = Date.now() + 8000;
        do {
            if (!alive()) return null;
            if (typeof host.ftDefinitionsForDisplay === 'function') return host.ftDefinitionsForDisplay;
            await new Promise(resolve => setTimeout(resolve, 100));
        } while (Date.now() < deadline);
        return null;
    };
    const schedule = (): void => {
        if (!running && timer === undefined && queued.size) timer = setTimeout(() => { timer = undefined; void flush(); }, 100);
    };
    const retryHook = (): void => {
        if (hookRetryTimer !== undefined) return;
        const delay = [8000, 16000, 32000][hookRetries++];
        if (delay === undefined || !visible.size) return;
        hookRetryTimer = setTimeout(() => {
            hookRetryTimer = undefined;
            if (!alive()) return;
            prune();
            for (const [id, target] of visible) if (onScreen(target) && !inFlight.has(id)) queued.set(id, target);
            schedule();
        }, delay);
    };
    const flush = async (): Promise<void> => {
        if (!alive()) return;
        prune();
        if (!queued.size) return;
        running = true;
        try {
            // A resolved null is not a permanent cache: the core bundle can arrive later.
            const fetchDefinitions = typeof host.ftDefinitionsForDisplay === 'function'
                ? host.ftDefinitionsForDisplay : await (hook ??= waitForHook());
            if (!alive()) return;
            prune(); // Visibility may have changed during the bounded hook wait.
            if (!fetchDefinitions) { hook = undefined; queued.clear(); retryHook(); return; }
            if (hookRetryTimer !== undefined || hookRetries > 0) {
                for (const [id, target] of visible)
                    if (onScreen(target) && !inFlight.has(id) && !settled.has(id)) queued.set(id, target);
                hookRetries = 0;
            }
            if (hookRetryTimer !== undefined) clearTimeout(hookRetryTimer);
            hookRetryTimer = undefined;
            const batch = [...queued.values()].filter(target => visible.has(target.id) && onScreen(target)).slice(0, 16);
            for (const [id, target] of queued) if (!onScreen(target)) { queued.delete(id); visible.delete(id); }
            if (!batch.length) return;
            for (const { id } of batch) { queued.delete(id); inFlight.add(id); }
            let answer: Awaited<ReturnType<DisplayDefinitions>> = {};
            let requested = batch;
            try { answer = await fetchDefinitions(batch.map(target => target.id)); } catch {
                // A transient display read gets one delayed retry; actual refusals remain terminal.
                await new Promise(resolve => setTimeout(resolve, 500));
                if (!alive()) return;
                prune();
                requested = batch.filter(target => targets.get(target.tr) === target && visible.has(target.id) && onScreen(target));
                if (requested.length) {
                    try { answer = await fetchDefinitions(requested.map(target => target.id)); } catch { /* saved after one retry */ }
                }
            }
            if (!alive()) return;
            for (const target of batch) {
                const { id, row, tr, td } = target;
                inFlight.delete(id);
                if (!requested.includes(target) || targets.get(tr) !== target || !attached(target)) {
                    const current = visible.get(id);
                    if (current && onScreen(current)) queued.set(id, current);
                    continue;
                }
                const def = answer[id];
                if (def) swapDefinitionRune(row, td, def);
                else td.dataset.ftRuneRefusal = 'definition-missing-or-refused';
                settled.set(id, td.dataset.ftRuneRefusal);
                observer?.unobserve(tr); targets.delete(tr); visible.delete(id); queued.delete(id);
            }
            prune();
        } finally {
            running = false;
            if (!stopped) schedule();
        }
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
        if (!alive()) return;
        prune();
        for (const entry of entries) {
            const target = targets.get(entry.target);
            if (!target) continue;
            const { id } = target;
            if (!entry.isIntersecting || !attached(target)) { queued.delete(id); visible.delete(id); continue; }
            visible.set(id, target);
            if (!inFlight.has(id)) queued.set(id, target);
        }
        schedule();
    }) : null;
    return (row, tr) => {
        const td = tr.cells[nameColumn];
        if (!td || stopped || targets.has(tr)) return;
        const id = row.item_id.toUpperCase();
        if (settled.has(id)) {
            const refusal = settled.get(id);
            td.dataset.ftRune = refusal ? 'saved' : 'exact';
            if (refusal) td.dataset.ftRuneRefusal = refusal;
            return;
        }
        td.dataset.ftRune = 'saved';
        if (!td.querySelector('.ft-rune-mount')) { td.dataset.ftRuneRefusal = 'root-definition-missing'; return; }
        if (!observer) { td.dataset.ftRuneRefusal = 'intersection-observer-unavailable'; return; }
        targets.set(tr, { id, row, tr, td });
        observer.observe(tr);
    };
}

// ── External row removal (no-reload delete) ───────────────────────────────────────────────────────
// A per-row action button (e.g. my_encounters' delete) soft-deletes on the server, then wants its row
// gone WITHOUT a full page reload. It can't touch the table's <tr> directly — the renderer keeps an
// in-memory model (originalRows / order / trByRow) the DOM is derived from, so a raw .remove() would
// desync it (sort would try to re-insert the detached node; the zebra/empty-state counts would drift).
// Instead the button calls window.ftCatalogRemoveRow(itemId), which dispatches this CustomEvent; each
// mounted table listens and removes the matching row THROUGH its model. Event name + dispatcher live
// here; index.ts wires the dispatcher onto window.
export const CATALOG_ROW_REMOVE_EVENT = 'ft-catalog-row-remove';

export function requestCatalogRowRemoval(itemId: string): void {
    if (!itemId) return;
    document.dispatchEvent(new CustomEvent(CATALOG_ROW_REMOVE_EVENT, { detail: { itemId } }));
}

// ── Segment rendering (the pure-data render path) ─────────────────────────────────────────────────
// A cell/header described as CellSegment[] is rendered HERE, client-side: icons resolve through the
// front-end iconMap() (core-helpers.js, reads window.ICON_LOOKUP) — the PHP endpoint ships ids/values,
// never icon_map() HTML. Mirrors list-display's iconForRef/iconMapOrType: iconMap() resolves named
// icons + NamedIcon UUIDs; a catalog-item UUID with its own art (not in ICON_LOOKUP) falls back to the
// data-shape `pic` url, then to the item_type icon.

// True when iconMap() couldn't resolve the key — i.e. it RENDERED the help-circle-outline fallback.
// Must inspect the actual <img src="…"> (double-quoted), NOT a bare substring match: every iconMap()
// output also carries help-circle-outline inside its onerror="this.src='…help-circle-outline.png'"
// handler, so a substring check is ALWAYS true and would discard correctly-resolved icons (e.g. a
// registry-resolved catalog UUID → its real .png), wrongly falling back to the generic item_type icon.
function isUnknownIcon(html: string): boolean {
    if (typeof html !== 'string' || html === '') return true;
    const m = html.match(/\ssrc="([^"]*)"/); // the real icon src (onerror uses this.src='…', single-quoted)
    // No <img src> → iconMap() returned a rune TYPE-BADGE (<svg> span) or its deferred ft-icon
    // placeholder for an ability-type key. That IS a resolved icon — NOT the help-circle fallback — so
    // don't trip the seg.pic / seg.item_type fallback (which would discard the badge).
    if (!m) return false;
    return m[1]?.indexOf('help-circle-outline') !== -1;
}

// Is this segment id a catalog UUID? Mirrors PHP icon_map()'s check_uuid() gate for stamping
// data-ft-info-item-id (mobile long-press → openItemInfo) on the .ft-icon-map wrapper.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(id: string): boolean {
    return typeof id === 'string' && UUID_RE.test(id);
}

// Stamp a text/badge segment's `tip` as an INSTANT Bootstrap tooltip — same mechanism the icon
// segment uses (data-bs-toggle + data-bs-original-title, driven by template.js's body delegation).
// Deliberately NOT a native title=, which produces the slow ~1-2s browser tooltip (see initTooltips()).
// Returns the element so callers can inline it into an appendChild().
function applyTip<T extends HTMLElement>(el: T, tip?: string): T {
    if (typeof tip === 'string' && tip !== '') {
        el.setAttribute('data-bs-toggle', 'tooltip');
        el.setAttribute('data-bs-original-title', tip);
    }
    return el;
}

// Append one icon segment's nodes to `holder` (a <span> or <a>). iconMap() output is trusted HTML
// (our own generator); a `pic` url is set via the DOM (img.src) so it can never inject markup.
function appendIcon(holder: HTMLElement, seg: IconSegment): void {
    const w = seg.w ?? 25;
    const h = seg.h ?? 25;
    const title = seg.title === undefined ? '' : seg.title;
    let html = typeof iconMap === 'function' ? iconMap(seg.id, w, h, title) : '';
    if (isUnknownIcon(html) && seg.pic) {
        const img = document.createElement('img');
        img.src = seg.pic;
        // Preserve the image's natural aspect ratio: fit it INSIDE a w×h box (the longer side scales to
        // the limit, the other follows) instead of forcing both dimensions, which squashed non-square
        // art (e.g. portrait creature thumbnails). pointerEvents:none lets a click fall through to the
        // wrapping <a> when the segment carries a `link`.
        img.style.maxWidth = `${w}px`;
        img.style.maxHeight = `${h}px`;
        img.style.width = 'auto';
        img.style.height = 'auto';
        img.draggable = false;
        img.style.pointerEvents = 'none';
        // Tooltip parity with PHP icon_map(): catalog art (a UUID with its own picture, not in
        // ICON_LOOKUP) still deserves the SAME instant Bootstrap hover label icon_map($uuid) gives it.
        // When the endpoint ships a label (`title`, e.g. the creature_type / creature_role name), wrap
        // the <img> in the same .ft-icon-map span icon_map() emits: data-bs-toggle + data-bs-original-
        // title drive the instant tooltip via template.js's body delegation (NO slow native title=),
        // and a UUID id stamps data-ft-info-item-id so mobile long-press opens item info. No label →
        // bare img (unchanged). `title === false` is an explicit "no tooltip" opt-out.
        const label = typeof seg.title === 'string' ? seg.title : '';
        if (label) {
            img.alt = label;
            const tip = document.createElement('span');
            tip.className = 'd-inline-block ft-icon-map';
            tip.setAttribute('data-bs-toggle', 'tooltip');
            tip.setAttribute('data-bs-original-title', label);
            if (isUuid(seg.id)) tip.setAttribute('data-ft-info-item-id', seg.id);
            tip.appendChild(img);
            holder.appendChild(tip);
        } else {
            img.alt = '';
            holder.appendChild(img);
        }
    } else {
        if (isUnknownIcon(html) && seg.item_type && typeof iconMap === 'function') {
            html = iconMap(seg.item_type, w, h, title);
        }
        const span = document.createElement('span');
        span.innerHTML = html;
        while (span.firstChild) holder.appendChild(span.firstChild);
    }
    if (seg.badge) {
        const badge = document.createElement('span');
        badge.className = 'position-absolute top-0 start-100 translate-middle badge border rounded-pill bg-success';
        badge.textContent = seg.badge;
        holder.appendChild(badge);
    }
}

// Whitelist the Bootstrap button colour an action segment may request, so a bad/injected `variant`
// can never inject an arbitrary class. Falls back to a neutral 'secondary'.
const ACTION_VARIANTS = new Set(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark']);
function actionVariant(variant: string | undefined): string {
    return variant && ACTION_VARIANTS.has(variant) ? variant : 'secondary';
}

// Copy `text` to the clipboard, preferring the async Clipboard API (staging/live are HTTPS) with a
// legacy execCommand fallback for any non-secure context. Pure best-effort: a failure is logged, never
// thrown into the click listener.
function copyTextToClipboard(text: string): void {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(text).catch((err) => {
            console.warn('[catalog-table] clipboard write failed', err);
        });
        return;
    }
    const temp = document.createElement('textarea');
    temp.value = text;
    temp.style.position = 'fixed';
    temp.style.opacity = '0';
    document.body.appendChild(temp);
    temp.select();
    try { document.execCommand('copy'); } catch (err) { console.warn('[catalog-table] copy fallback failed', err); }
    temp.remove();
}

// Briefly swap a copy button's contents to a "Copied!" label, then restore — the inline feedback the
// legacy table gave (it wrote "Copied!" into a sibling div). Guarded against double-clicks via a flag.
function flashCopied(el: HTMLElement): void {
    if (el.dataset.ftCopying === '1') return;
    el.dataset.ftCopying = '1';
    const prev = el.innerHTML;
    el.innerHTML = '<span class="small">Copied!</span>';
    setTimeout(() => {
        el.innerHTML = prev;
        delete el.dataset.ftCopying;
    }, 1200);
}

// Dispatch a whitelisted segment action to its real window global. Each is guarded so a page that
// didn't load the handler degrades to a no-op (logged) instead of throwing inside the click listener.
// `el` (the button) is passed so an action can give inline feedback (copy → "Copied!").
function runSegmentAction(action: 'audio' | 'roll' | 'copy', arg: string, el?: HTMLElement): void {
    if (action === 'audio') {
        if (typeof playAudioID === 'function') { void playAudioID(arg); }
        else console.warn('[catalog-table] playAudioID unavailable for audio action');
        return;
    }
    if (action === 'roll') {
        // The only `roll` segment is a random table's (random_tables_list_inc.php) — Lane N2 RT-3: the browser rolls it.
        const tableRoll = (window as unknown as { ftRollRandomTableItem?: (id: string) => Promise<void> }).ftRollRandomTableItem;
        if (typeof tableRoll === 'function') { void tableRoll(arg); }
        else if (typeof useAbility === 'function') { void useAbility(arg); }
        else console.warn('[catalog-table] useAbility unavailable for roll action');
        return;
    }
    if (action === 'copy') {
        copyTextToClipboard(arg);
        if (el) flashCopied(el);
        return;
    }
}

function renderSegments(segments: CellSegment[]): DocumentFragment {
    const frag = document.createDocumentFragment();
    for (const seg of segments) {
        switch (seg.kind) {
            case 'text': {
                if (seg.link) {
                    const a = document.createElement('a');
                    a.href = seg.link;
                    a.className = seg.cls || 'link-primary';
                    if (seg.target) a.target = seg.target;
                    a.textContent = seg.value;
                    frag.appendChild(applyTip(a, seg.tip));
                } else if (seg.cls || seg.tip) {
                    const s = document.createElement('span');
                    if (seg.cls) s.className = seg.cls;
                    s.textContent = seg.value;
                    frag.appendChild(applyTip(s, seg.tip));
                } else {
                    frag.appendChild(document.createTextNode(seg.value));
                }
                break;
            }
            case 'icon': {
                // Whitelisted click ACTION → render the icon inside a <button> that fires a known
                // global handler (pure data: the payload carries the action name + arg, never an
                // inline onclick). Otherwise the legacy <a>/<span> holder.
                if (seg.action && seg.actionArg) {
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = `btn btn-${actionVariant(seg.variant)} position-relative d-inline-block`;
                    if (seg.cls) for (const c of seg.cls.split(/\s+/)) if (c) btn.classList.add(c);
                    appendIcon(btn, seg);
                    const action = seg.action;
                    const arg = seg.actionArg;
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        runSegmentAction(action, arg, btn);
                    });
                    frag.appendChild(btn);
                    break;
                }
                const holder = document.createElement(seg.link ? 'a' : 'span');
                holder.classList.add('position-relative', 'd-inline-block');
                if (seg.cls) for (const c of seg.cls.split(/\s+/)) if (c) holder.classList.add(c);
                if (seg.link) {
                    (holder as HTMLAnchorElement).href = seg.link;
                    if (seg.target) (holder as HTMLAnchorElement).target = seg.target;
                }
                appendIcon(holder, seg);
                frag.appendChild(holder);
                break;
            }
            case 'badge': {
                const b = document.createElement('span');
                b.className = `badge bg-${seg.variant || 'secondary'}`;
                if (seg.cls) b.className += ` ${seg.cls}`;
                b.textContent = seg.value;
                frag.appendChild(applyTip(b, seg.tip));
                break;
            }
            case 'br':
                frag.appendChild(document.createElement('br'));
                break;
            case 'raw': {
                const s = document.createElement('span');
                s.innerHTML = seg.html; // trusted escape hatch
                frag.appendChild(s);
                break;
            }
            case 'node':
                frag.appendChild(seg.build());
                break;
            case 'parsetext': {
                // Rich Quill text rendered client-side via the shared parseText() port (retires PHP
                // parse_text()). [table:]/[random_table:] tags become .ft-pt-table-mount placeholders
                // hydrated by the ftMountParseTextTables(container) re-invoke after the build.
                const s = document.createElement('span');
                s.innerHTML = parseText(seg.text);
                frag.appendChild(s);
                break;
            }
            case 'thumbnail': {
                // Pure-data thumbnail: thumbnail.ts selects the URL from arr.pictures and builds the
                // <img>/initials node (no DB, no get_thumbnail()). Wrap in an inline-block span so the
                // cell's text-center centers it (same reason the legacy raw fragment was wrapped).
                const span = document.createElement('span');
                span.className = 'd-inline-block';
                span.appendChild(buildThumbnailNode(seg));
                frag.appendChild(span);
                break;
            }
        }
    }
    return frag;
}

// ── External filter panel (spells/mentas) ────────────────────────────────────────────────────────
// The card renderer (mountCatalogList) has its own panel logic; the table renderer needs its own
// because rows carry their filterable values in `row.filters` rather than on a flat card object.

// Normalize a row filter value to an array, KEEPING "0". A plain Boolean filter would drop "0",
// but spell level 0 (cantrips) is a real value — so only empty string / null / undefined are dropped.
function toFilterArray(v: string | string[] | undefined): string[] {
    if (Array.isArray(v)) return v.filter((x) => x !== '' && x != null);
    return v === '' || v == null ? [] : [v];
}

interface ControlState {
    isCheckbox: boolean;
    checked: boolean;
    values: string[]; // selected non-empty values for selects/inputs ([] = "no filter")
}

// Read one filter-panel control by element id. Handles checkboxes (render_checkbox → <input
// type=checkbox value="1">), <select multiple> (Tom Select keeps the underlying select in sync), and
// single <select>/<input>. Empty selections collapse to values=[] so they impose no constraint.
function readControl(elementId: string): ControlState {
    const el = document.getElementById(elementId) as HTMLInputElement | HTMLSelectElement | null;
    if (!el) return { isCheckbox: false, checked: false, values: [] };
    if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        return { isCheckbox: true, checked: el.checked, values: [] };
    }
    if (el instanceof HTMLSelectElement && el.multiple) {
        return {
            isCheckbox: false,
            checked: false,
            values: Array.from(el.selectedOptions).map((o) => o.value).filter((x) => x !== ''),
        };
    }
    const v = (el as HTMLInputElement | HTMLSelectElement).value ?? '';
    return { isCheckbox: false, checked: false, values: v === '' ? [] : [v] };
}

// A row passes the panel when EVERY active control is satisfied. A checkbox is active only when
// checked (then the row's value must be "1"); a select/input is active only when something is chosen
// (then the row must carry at least one of the chosen values). filterFields maps elementId → row key.
// A numeric min/max bound from a "range:<minId>:<maxId>" field. The row's value (a single numeric
// string under the row key) must fall within whichever of the two inputs is filled. An active bound on
// a row that has NO value excludes it (filtering "by dice/cost" means the row must have that attribute).
function matchesRange(spec: string, rowVal: string | string[] | undefined): boolean {
    const [, minId, maxId] = spec.split(':');
    const minEl = minId ? (document.getElementById(minId) as HTMLInputElement | null) : null;
    const maxEl = maxId ? (document.getElementById(maxId) as HTMLInputElement | null) : null;
    const minStr = (minEl?.value ?? '').trim();
    const maxStr = (maxEl?.value ?? '').trim();
    if (minStr === '' && maxStr === '') return true; // no bound set → no constraint
    const n = parseFloat(String(toFilterArray(rowVal)[0] ?? ''));
    if (Number.isNaN(n)) return false; // a bound is active but the row carries no value
    if (minStr !== '') { const lo = parseFloat(minStr); if (!Number.isNaN(lo) && n < lo) return false; }
    if (maxStr !== '') { const hi = parseFloat(maxStr); if (!Number.isNaN(hi) && n > hi) return false; }
    return true;
}

function matchesPanel(row: TableRow, filterFields: Record<string, string>): boolean {
    const filters = row.filters || {};
    for (const [param, elementId] of Object.entries(filterFields)) {
        // Numeric range field: the value is "range:<minInputId>:<maxInputId>" rather than a control id.
        if (elementId.startsWith('range:')) {
            if (!matchesRange(elementId, filters[param])) return false;
            continue;
        }
        // Presence-exclusion toggle (e.g. spells' header "Hide class/subclass-locked"): `param` is the
        // toggle CHECKBOX's element id and the spec lists the row.filters keys to test —
        // "exclude-present:requires_class:requires_subclass". When the box is checked, any row that
        // carries a non-empty value under ANY listed key is hidden. This is the INVERSE of a value-match
        // select: it excludes rows that HAVE the attribute at all, rather than narrowing to a value.
        if (elementId.startsWith('exclude-present:')) {
            const cb = document.getElementById(param);
            if (cb instanceof HTMLInputElement && cb.checked) {
                for (const key of elementId.split(':').slice(1)) {
                    if (toFilterArray(filters[key]).length > 0) return false;
                }
            }
            continue;
        }
        const ctrl = readControl(elementId);
        const rowVals = toFilterArray(filters[param]);
        if (ctrl.isCheckbox) {
            if (ctrl.checked && !rowVals.includes('1')) return false;
        } else if (ctrl.values.length && !ctrl.values.some((s) => rowVals.includes(s))) {
            return false;
        }
    }
    return true;
}

// Close the collapsible filter panel after Apply. Force-HIDE (not slideToggleEl): Apply is only ever
// clicked while the panel is open, so "close" must mean close — never toggle. This also makes a SHARED
// panel safe when a page mounts MULTIPLE tables against the same Apply button (e.g. creatures.php's
// Core + Named tables): each mount's Apply listener fires, and N force-hides collapse to the same
// closed state, whereas N slideToggles would flip it back open on the even calls.
function closePanel(panelId: string | null): void {
    if (!panelId) return;
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

// Match the existing tablesorter init: dark_mode cookie → dark theme, else blue.
function themeClass(): 'tablesorter-dark' | 'tablesorter-blue' {
    const m = document.cookie.match(/(?:^|;\s*)dark_mode=([^;]*)/);
    return m && decodeURIComponent(m[1] ?? '') === '1' ? 'tablesorter-dark' : 'tablesorter-blue';
}

// ── PLT cells awaiting a browser PL (cell.pl — encounterDisplayCells) ─────────────────────────────
// PL is priced ONLY in the browser. A pending PLT cell shows the shared `ft-pl-pending` treatment (never the partial stored
// sum), sorts after every number in either direction, and is replaced by the number once pricePendingPl() has a browser
// price for every one of its rows (shared/pl-browser-pricing.ts — the same engine path as the adventure page's PLT).
function plInputOf(cell: TableCell): PlTotalInput {
    return { stored: cell.pl?.stored ?? 0, pendingIds: cell.pl?.pending_ids ?? [], pendingOther: cell.pl?.pending_other ?? 0 };
}

function isPlPending(cell: TableCell | undefined): boolean {
    return !!cell && cell.pl !== undefined;
}

function plPendingNode(cell: TableCell, state: PlState): HTMLElement {
    const badge = document.createElement('span');
    badge.className = 'badge rounded-pill bg-light text-muted ft-pl-pending';
    badge.dataset.ftPlState = state;
    badge.textContent = state === 'pending' ? 'PL unavailable: browser-price-not-held' : '\u2014';
    return applyTip(badge, plPendingTip(plInputOf(cell), state));
}

// Fill a <td> from its cell model (the one render path for the initial build AND a PLT repaint).
function fillCell(td: HTMLTableCellElement, cell: TableCell | undefined, plState?: PlState): void {
    td.textContent = '';
    if (cell && cell.pl !== undefined) {
        td.dataset.ftPlCell = 'pending';
        td.appendChild(plPendingNode(cell, plState ?? (plPriceable(plInputOf(cell)) ? 'pricing' : 'pending')));
        return;
    }
    if (cell?.segments) {
        td.appendChild(renderSegments(cell.segments));
    } else {
        td.innerHTML = cell?.html ?? '';
    }
}

function compareRows(a: TableRow, b: TableRow, col: TableColumn): number {
    const av = a.cells[col.key]?.sort;
    const bv = b.cells[col.key]?.sort;
    if (col.sort_type === 'num') {
        const an = typeof av === 'number' ? av : parseFloat(String(av ?? ''));
        const bn = typeof bv === 'number' ? bv : parseFloat(String(bv ?? ''));
        const aa = Number.isNaN(an) ? -Infinity : an;
        const bb = Number.isNaN(bn) ? -Infinity : bn;
        return aa - bb;
    }
    return String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

export function mountCatalogTable(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-table] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(definitionListDataUrl(resolveUrl(config.endpoint)), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            // Read the body as text first so a non-JSON response (a stray PHP warning, an HTML 500
            // page) surfaces a useful console message instead of an opaque "unexpected character".
            const raw = await res.text();
            let data: CatalogTableResponse;
            try {
                data = JSON.parse(raw) as CatalogTableResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            // The endpoint's shutdown handler returns { error, detail } on a fatal — bubble its detail.
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            const adoptRawList = rawListAdopter((data as unknown as { schema?: unknown }).schema);
            if (adoptRawList) data = await adoptRawList(data, root, resolveUrl(config.endpoint));
            if (isBaseDefinitionListSchema((data as unknown as BaseDefinitionListResponse).schema)) {
                performance.mark('ft-catalog-adopt-start');
                data = await adoptBaseDefinitionList(data as unknown as BaseDefinitionListResponse,
                    (done, total) => setStatus(root, `Preparing ${done} of ${total} definitions…`));
                performance.mark('ft-catalog-adopt-end');
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            if (isBaseDefinitionListSchema(data.definition_schema)) performance.mark('ft-catalog-build-start');
            build(root, container, config, data, searchInput);
            if (isBaseDefinitionListSchema(data.definition_schema)) performance.mark('ft-catalog-build-end');
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-table] fetch/render failed', e);
            // Render the message INSIDE the results grid (top of the reserved min-height space) rather
            // than the status line below it, which would sit far below the empty grid.
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'text-center text-muted py-4';
            msg.textContent = 'Could not load results. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    config: CatalogListConfig,
    data: CatalogTableResponse,
    searchInput: HTMLInputElement | null,
): void {
    const columns = data.columns || [];
    const creatureData = data as CatalogTableResponse & { client_catalog?: Record<string, unknown>; size_types?: Record<string, unknown>[] };
    hydrateCreatureCells(data.rows ?? [], creatureData.client_catalog ?? {}, creatureData.size_types ?? []);
    const originalRows = data.rows || []; // preserved order = stable-sort tiebreak
    for (const row of originalRows) {
        const display = (row as TableRow & { encounter_display?: EncounterDisplayInput }).encounter_display;
        if (display) Object.assign(row.cells, encounterDisplayCells(display));
    }

    container.innerHTML = '';

    const wrap = document.createElement('div');
    wrap.className = 'table-responsive-xxl';
    const table = document.createElement('table');
    // NOTE: deliberately NO bare `tablesorter` class. That class is what the legacy jQuery widget
    // (applyTablesorterToAllTables() in template.js → `$('.tablesorter').tablesorter(...)`) scans for,
    // and it would double-init our table — injecting a SECOND filter row (the intermittent "two search
    // rows" depending on whether its init won the race against our async build). The theme is keyed on
    // `.tablesorter-blue`/`-dark` (not bare `.tablesorter`), so the look is fully preserved without it.
    table.className = `${themeClass()} ft-catalog-table`;
    if (isBaseDefinitionListSchema(data.definition_schema)) {
        table.dataset.ftDefinitionSchema = data.definition_schema;
        table.dataset.ftDefinitionTotal = String(data.total);
    } else if (creatureData.client_catalog?.schema === DEFINITION_FAMILY_SCHEMA) {
        table.dataset.ftDefinitionSchema = DEFINITION_FAMILY_SCHEMA;
        table.dataset.ftDefinitionFamily = String(creatureData.client_catalog.family);
        table.dataset.ftDefinitionSelectionTotal = String(data.total);
        table.dataset.ftDefinitionTotal = String(originalRows.length);
    }
    wrap.appendChild(table);
    container.appendChild(wrap);

    // ── Header (sortable) + filter row ────────────────────────────────────────
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    headerRow.className = 'tablesorter-headerRow';
    const filterRow = document.createElement('tr');
    filterRow.className = 'tablesorter-filter-row';

    const filterInputs: Record<string, HTMLInputElement> = {};
    let sort: SortState | null = null;

    columns.forEach((col) => {
        const th = document.createElement('th');
        if (col.align) th.classList.add(`text-${col.align}`);
        if (col.width) th.style.width = col.width;
        if (col.header_segments) {
            th.appendChild(renderSegments(col.header_segments));
        } else {
            th.innerHTML = col.header_html || '';
        }
        // a11y only — NO native `title=` here. A native title produces the slow (~1-2s) browser
        // tooltip; icon headers already carry an INSTANT Bootstrap label on their inner .ft-icon-map
        // span (shown via template.js's body-delegation getOrCreateInstance().show()), and text
        // headers show their label inline. aria-label keeps the screen-reader name without the lag.
        if (col.header_label) th.setAttribute('aria-label', col.header_label);

        if (col.sortable) {
            th.classList.add('tablesorter-header', 'tablesorter-headerUnSorted');
            th.setAttribute('role', 'button');
            th.setAttribute('tabindex', '0');
            th.setAttribute('aria-label', `Sort by ${col.header_label}`);
            const onSort = () => applySort(col.key);
            th.addEventListener('click', onSort);
            th.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(); }
            });
        } else {
            th.classList.add('sorter-false');
        }
        headerRow.appendChild(th);

        const ftd = document.createElement('td');
        if (col.filterable) {
            const input = document.createElement('input');
            input.type = 'search';
            input.className = 'tablesorter-filter';
            input.autocomplete = 'off';
            input.setAttribute('aria-label', `Filter ${col.header_label}`);
            input.addEventListener('input', applyFilters);
            filterInputs[col.key] = input;
            ftd.appendChild(input);
        }
        filterRow.appendChild(ftd);
    });
    thead.appendChild(headerRow);
    // Only when some column filters: an all-unfilterable spec (the encounter lists) otherwise paints an empty
    // strip under the header. Same rule as ft-sortable.ts.
    if (columns.some(col => col.filterable)) thead.appendChild(filterRow);
    table.appendChild(thead);

    // ── Body: one <tr> per row ────────────────────────────────────────────────
    // Default (eager) mode: every row's <tr> is built up front; sort reorders, filter toggles display.
    // PROGRESSIVE mode: rows are built LAZILY for the current filtered subset, one page at a time — the
    // first page on load, the next whenever the bottom of the table scrolls near the viewport. Search/sort
    // still run client-side over the full in-memory dataset. On for an explicit page size
    // (config.tableCap — the audio library, whose inline <audio> rows hang the tab when all materialized)
    // and automatically for any table over FT_TABLE_AUTO_PROGRESSIVE_ROWS (2026-09-15: passive_abilities.php
    // spent ~13 s drawing 10,871 rows before the page was usable). `?ft_table_eager=1` forces eager mode for
    // tooling that reads every row out of the DOM.
    const FT_TABLE_AUTO_PROGRESSIVE_ROWS = 600;
    const FT_TABLE_PAGE_ROWS = 200;
    const forceEager = new URLSearchParams(window.location.search).has('ft_table_eager');
    const cap = forceEager
        ? null
        : (config.tableCap && config.tableCap > 0
            ? config.tableCap
            : (originalRows.length > FT_TABLE_AUTO_PROGRESSIVE_ROWS ? FT_TABLE_PAGE_ROWS : null));
    if (cap) table.dataset.ftTableProgressive = String(cap);
    const tbody = document.createElement('tbody');
    const trByRow = new Map<TableRow, HTMLTableRowElement>();
    // Once the browser PL pricing has finished, a PLT still pending stays pending: a row built LATER (progressive mode) must say so
    // rather than "pricing in your browser".
    let plPhase: PlState | undefined;

    // Build (and memoize) a row's <tr>. Memoizing means a row that scrolls in/out of the capped
    // subset — or gets re-sorted — keeps its node and any playing <audio> state.
    const observeRune = data.definition_schema === SAVED_BASE_DEFINITION_LIST_SCHEMA
        ? observeDefinitionRunes(table, columns.findIndex(col => col.key === 'name')) : null;
    function buildTr(row: TableRow): HTMLTableRowElement {
        const cached = trByRow.get(row);
        if (cached) { observeRune?.(row, cached); return cached; }
        const tr = document.createElement('tr');
        // Stamp the row's item_id so it's identifiable in the DOM (and matches the model lookup that
        // removeRowByItemId / ftCatalogRemoveRow use for no-reload deletes).
        if (row.item_id) tr.dataset.ftRowItemId = row.item_id;
        columns.forEach((col) => {
            const td = document.createElement('td');
            if (col.align) td.classList.add(`text-${col.align}`);
            td.classList.add('align-middle');
            fillCell(td, row.cells[col.key], plPhase);
            tr.appendChild(td);
        });
        trByRow.set(row, tr);
        observeRune?.(row, tr);
        // Capped mode builds rows LAZILY as searches surface them, AFTER the one-shot hydration passes
        // at the end of build() have already run over the initial container. So a freshly-built row must
        // hydrate its own icon_ph placeholders (e.g. the audio delete/confirm buttons) + rune/parsetext
        // mounts now, or they'd render as empty placeholders. Idempotent with the build-tail passes, so
        // the initially-shown rows (built during the first applyFilters) are unaffected. Eager mode skips
        // this — its rows exist before the tail passes, which hydrate them all. Tooltips are covered by
        // template.js's body-level hover delegation, so no per-row initTooltips is needed here.
        if (cap) {
            window.ftMountEncounterDisplays?.(tr);
            if (typeof window.ftMountRuneIcons === 'function') { try { window.ftMountRuneIcons(tr); } catch { /* tail/footer pass covers it */ } }
            if (typeof window.ftMountIcons === 'function') { try { window.ftMountIcons(tr); } catch { /* tail/footer pass covers it */ } }
            try { ftMountParseTextTables(tr); } catch { /* tail/footer pass covers it */ }
        }
        return tr;
    }

    if (!cap) {
        originalRows.forEach((row) => tbody.appendChild(buildTr(row)));
    }

    const emptyRow = document.createElement('tr');
    emptyRow.className = 'ft-table-empty-row';
    const emptyCell = document.createElement('td');
    emptyCell.colSpan = Math.max(1, columns.length);
    emptyCell.className = 'text-center text-muted py-4';
    // A list with no rows at all may opt into the themed empty state; filtering never brings rows back,
    // so a list that HAS rows keeps the plain line for a search that misses.
    const emptySpec = originalRows.length === 0 ? emptyStateFromAttributes(root, 'No results found.') : null;
    if (emptySpec) emptyCell.replaceChildren(buildEmptyState(emptySpec));
    else emptyCell.textContent = 'No results found.';
    emptyRow.style.display = 'none';
    emptyRow.appendChild(emptyCell);
    tbody.appendChild(emptyRow);
    table.appendChild(tbody);

    // Capped mode: a small notice above the table telling the user the render was truncated (so a
    // huge unfiltered list isn't silently cut off — it prompts them to search). Hidden until it fires.
    const capNotice = cap ? document.createElement('div') : null;
    if (capNotice) {
        capNotice.className = 'text-center text-muted small py-2';
        capNotice.style.display = 'none';
        container.insertBefore(capNotice, wrap);
    }
    // Partial payload (the endpoint's compile budget ran out on a cold cache): say so, never cut silently.
    const partial = buildIncompleteNotice(data.incomplete);
    if (partial) container.insertBefore(partial, capNotice ?? wrap);
    if (data.definition_refusals?.length) {
        const notice = document.createElement('div');
        notice.className = 'alert alert-warning';
        notice.dataset.ftDefinitionRefusals = String(data.definition_refusals.length);
        notice.textContent = `${data.definition_refusals.length} definitions have unsupported browser stages; their named reasons appear beside their names.`;
        container.insertBefore(notice, wrap);
    }
    // The capped subset currently in the DOM (detached + rebuilt on each filter/sort pass).
    let shownTrs: HTMLTableRowElement[] = [];

    // Current row order (mutated by sort); filtering reads this to re-stripe visible rows.
    let order = originalRows.slice();

    const filterFields = config.filterFields || {};
    const hasPanel = Object.keys(filterFields).length > 0;

    // Does one row pass the current keyword + per-column + panel filters? Shared by both render paths.
    function rowMatches(row: TableRow, keyword: string, colFilters: ReadonlyArray<readonly [string, string]>): boolean {
        let show = !hasPanel || matchesPanel(row, filterFields);
        if (show && keyword) {
            show = columns.some((c) => (row.cells[c.key]?.text || '').includes(keyword));
        }
        if (show) {
            for (const [key, val] of colFilters) {
                if (!(row.cells[key]?.text || '').includes(val)) { show = false; break; }
            }
        }
        return show;
    }

    // PROGRESSIVE render: the full matched list is computed (cheap — no DOM), but only rows up to the
    // current page limit are built. A filter/sort resets to the first page; scrolling near the table's end
    // appends the next page. buildTr() memoizes, so a re-shown row reuses its node.
    let matchedRows: TableRow[] = [];
    function updateCapNotice(): void {
        if (!capNotice) return;
        if (matchedRows.length > shownTrs.length) {
            capNotice.style.display = '';
            capNotice.textContent = `Showing ${shownTrs.length} of ${matchedRows.length} matches — scroll down for more, or search to narrow it down.`;
        } else {
            capNotice.style.display = 'none';
        }
    }
    function appendNextPage(): void {
        const end = Math.min(matchedRows.length, shownTrs.length + (cap as number));
        let stripeOdd = shownTrs.length % 2 === 0;
        const frag = document.createDocumentFragment();
        for (let i = shownTrs.length; i < end; i++) {
            const tr = buildTr(matchedRows[i] as TableRow);
            tr.style.display = '';
            tr.classList.toggle('odd', stripeOdd);
            tr.classList.toggle('even', !stripeOdd);
            stripeOdd = !stripeOdd;
            frag.appendChild(tr);
            shownTrs.push(tr);
        }
        tbody.insertBefore(frag, emptyRow);
        updateCapNotice();
    }
    function applyFiltersCapped(keyword: string, colFilters: ReadonlyArray<readonly [string, string]>): void {
        for (const tr of shownTrs) tr.remove();
        shownTrs = [];
        matchedRows = order.filter((row) => rowMatches(row, keyword, colFilters));
        emptyRow.style.display = matchedRows.length === 0 ? '' : 'none';
        appendNextPage();
        rearmSentinel();
    }

    // Scroll sentinel just below the table: when it comes within ~1,000 px of the viewport, append the
    // next page. Re-observing after each append makes the observer re-report a sentinel that is STILL in
    // range (a tall screen, or a short page of matches), so pages keep filling until it leaves range.
    const sentinel = cap ? document.createElement('div') : null;
    let sentinelObserver: IntersectionObserver | null = null;
    function rearmSentinel(): void {
        if (!sentinel || !sentinelObserver) return;
        sentinelObserver.unobserve(sentinel);
        if (shownTrs.length < matchedRows.length) sentinelObserver.observe(sentinel);
    }
    if (sentinel) {
        sentinel.setAttribute('data-ft-table-sentinel', '');
        sentinel.style.height = '1px';
        wrap.after(sentinel);
        if (typeof IntersectionObserver === 'function') {
            sentinelObserver = new IntersectionObserver((entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;
                if (!document.contains(table) || shownTrs.length >= matchedRows.length) return;
                appendNextPage();
                rearmSentinel();
            }, { rootMargin: '0px 0px 1000px 0px' });
        }
    }

    function applyFilters(): void {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const colFilters = Object.entries(filterInputs)
            .map(([key, el]) => [key, el.value.trim().toLowerCase()] as const)
            .filter(([, v]) => v !== '');

        if (cap) { applyFiltersCapped(keyword, colFilters); return; }

        let visible = 0;
        let stripeOdd = true;
        for (const row of order) {
            const tr = trByRow.get(row);
            if (!tr) continue;
            const show = rowMatches(row, keyword, colFilters);
            // Hide inline rather than via the `.tablesorter .filtered { display:none }` rule — we no
            // longer carry the bare `tablesorter` class (see table.className above), so that selector
            // wouldn't match. Zebra (tr.odd/.even) still works: it's keyed on `.tablesorter-blue/-dark`.
            tr.style.display = show ? '' : 'none';
            if (show) {
                tr.classList.toggle('odd', stripeOdd);
                tr.classList.toggle('even', !stripeOdd);
                stripeOdd = !stripeOdd;
                visible++;
            }
        }
        emptyRow.style.display = visible === 0 ? '' : 'none';
    }

    function applySort(key: string): void {
        const col = columns.find((c) => c.key === key);
        if (!col || !col.sortable) return;

        const dir: 'asc' | 'desc' = sort && sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
        sortBy(col, dir);
    }

    function sortBy(col: TableColumn, dir: 'asc' | 'desc'): void {
        const key = col.key;
        sort = { key, dir };

        // Stable sort: decorate with original index so equal keys keep payload order. A PLT still awaiting its browser
        // price sorts after every number in BOTH directions (it has no value yet — never read as 0).
        order = originalRows
            .map((row, i) => ({ row, i }))
            .sort((a, b) => {
                const ap = isPlPending(a.row.cells[key]);
                const bp = isPlPending(b.row.cells[key]);
                if (ap !== bp) return ap ? 1 : -1;
                const cmp = compareRows(a.row, b.row, col);
                return (dir === 'asc' ? cmp : -cmp) || (a.i - b.i);
            })
            .map((x) => x.row);

        // Reflect order in the DOM (emptyRow stays last). In capped mode the DOM is rebuilt from
        // `order` by applyFilters() below, so skip the eager reorder (it would re-attach cached rows
        // that the current filter has excluded).
        if (!cap) {
            for (const row of order) {
                const tr = trByRow.get(row);
                if (tr) tbody.insertBefore(tr, emptyRow);
            }
        }

        // Header arrow state (tablesorter theme classes).
        headerRow.querySelectorAll('th').forEach((th, idx) => {
            const c = columns[idx];
            if (!c?.sortable) return;
            th.classList.toggle('tablesorter-headerAsc', c.key === key && dir === 'asc');
            th.classList.toggle('tablesorter-headerDesc', c.key === key && dir === 'desc');
            th.classList.toggle('tablesorter-headerUnSorted', c.key !== key);
            th.setAttribute('aria-sort', c.key === key ? (dir === 'asc' ? 'ascending' : 'descending') : 'none');
        });

        applyFilters(); // re-stripe in the new order
    }

    // ── No-reload row removal (ftCatalogRemoveRow → CATALOG_ROW_REMOVE_EVENT) ──
    // Drop the row from every structure (originalRows for the sort tiebreak, the live `order`, the
    // trByRow map), detach its <tr>, then re-run applyFilters() to re-stripe the survivors and refresh
    // the empty-state. Returns true when a row matched, so the listener only acts on its owning table.
    function removeRowByItemId(itemId: string): boolean {
        const idx = originalRows.findIndex((r) => r.item_id === itemId);
        if (idx === -1) return false;
        const row = originalRows[idx];
        if (!row) return false;
        originalRows.splice(idx, 1);
        order = order.filter((r) => r !== row);
        const tr = trByRow.get(row);
        if (tr) tr.remove();
        trByRow.delete(row);
        applyFilters();
        return true;
    }

    document.addEventListener(CATALOG_ROW_REMOVE_EVENT, (e) => {
        // A table replaced by a re-mount is detached from the document; its stale listener goes inert.
        if (!document.contains(table)) return;
        const itemId = (e as CustomEvent<{ itemId?: string }>).detail?.itemId;
        if (itemId) removeRowByItemId(itemId);
    });

    // ── Global keyword search box (toolbar) + its spyglass/clear trailing icons ──
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = () => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilters(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilters();
            });
        }
        syncTrailing();
    }

    // ── External filter panel "Apply" button (spells/mentas) ──
    // The bundle owns the apply behavior: close the panel, then re-run the client-side filter against
    // the in-memory rows. The page's button must NOT carry its own onclick (the legacy queryDataInputs
    // server round-trip is gone) — this listener is the whole interaction.
    if (config.filterButtonId) {
        const applyBtn = document.getElementById(config.filterButtonId);
        if (applyBtn) {
            applyBtn.addEventListener('click', () => {
                closePanel(config.filterPanelId);
                applyFilters();
            });
        }
    }

    // Header quick-toggles declared as `exclude-present:` panel fields (e.g. spells' "Hide class/
    // subclass-locked") live OUTSIDE the collapsible panel, so — unlike the panel's selects which wait
    // for the Apply button — they re-filter the in-memory rows immediately on change.
    for (const [param, spec] of Object.entries(filterFields)) {
        if (!spec.startsWith('exclude-present:')) continue;
        const toggle = document.getElementById(param);
        if (toggle) toggle.addEventListener('change', applyFilters);
    }

    // Footer "Clear all": reset the panel's controls + re-stripe (panel stays open). Shared helper so
    // every renderer behaves identically.
    wireFilterClearButton(config, applyFilters);

    applyFilters(); // initial stripe

    // ── PLT: price the pending rows in the browser (async; the table is usable meanwhile) ──
    void pricePendingPl().catch(() => { table.dataset.ftPlt = 'error'; });

    async function pricePendingPl(): Promise<void> {
        const pending: Array<{ row: TableRow; key: string; col: number }> = [];
        columns.forEach((col, idx) => {
            for (const row of originalRows) if (isPlPending(row.cells[col.key])) pending.push({ row, key: col.key, col: idx });
        });
        if (!pending.length) return;
        // a row with a PC pending can never total here, so its ids are not worth pricing
        const ids = plDistinctPendingIds(pending.map((p) => plInputOf(p.row.cells[p.key] as TableCell)).filter((input) => (input.pendingOther ?? 0) === 0));
        table.dataset.ftPlt = 'pricing';
        table.dataset.ftPltRequested = String(ids.length);
        const priced = new Map<string, number>();
        const plKeys = new Set(pending.map((p) => p.key));
        const repaint = (state: PlState): void => {
            for (const p of pending) {
                const cell = p.row.cells[p.key];
                if (!cell || cell.pl === undefined) continue; // already resolved
                const definition = p.key === 'pl' && (isBaseDefinitionListSchema(data.definition_schema)
                    || creatureData.client_catalog?.schema === DEFINITION_FAMILY_SCHEMA);
                const total = definition ? priced.get(p.row.item_id.toUpperCase()) ?? null : plTotalOf(plInputOf(cell), priced);
                const td = trByRow.get(p.row)?.cells[p.col] as HTMLTableCellElement | undefined;
                if (total === null) {
                    if (td) fillCell(td, cell, state);
                    continue;
                }
                // resolved: the model becomes a plain numeric cell (sort + filter read it), the <td> follows
                const value = String(total);
                p.row.cells[p.key] = definition ? definitionPlCell(total, p.row.item_id) : { segments: [{ kind: 'text', value }], sort: total, text: value };
                if (p.key === 'pl' && p.row.filters) p.row.filters.pl = value;
                if (td) {
                    fillCell(td, p.row.cells[p.key]);
                    td.dataset.ftPlCell = 'priced';
                }
            }
        };
        const hooks = ids.length ? await waitForPlHooks() : null;
        if (hooks) {
            // a signed-in viewer saves what it prices (pl_claim_inc.php needs a session). Read from the page, not only the payload: a
            // definition list's payload is cached across viewers (ft_catalog_cache_payload_json), so it cannot carry a per-viewer flag.
            const signedIn = String((window as unknown as { FateTide?: { user?: { id?: unknown } } }).FateTide?.user?.id ?? '') !== '';
            await priceDefinitionsInBrowser(hooks, ids, data.pl_persist === true || signedIn, (values) => {
                if (!document.contains(table)) return;
                for (const [id, value] of values) priced.set(id, value);
                repaint('pricing');
            });
        }
        if (!document.contains(table)) return;
        plPhase = 'pending';
        repaint('pending');
        // re-apply the viewer's current sort when it is on a PLT column (the new numbers take their place), else re-stripe
        // (progressive mode: keep as many rows materialized as the viewer had scrolled into, so nothing vanishes under them)
        const keepShown = shownTrs.length;
        const current = sort;
        const sortCol = current ? columns.find((c) => c.key === current.key) : undefined;
        if (current && sortCol && plKeys.has(current.key)) sortBy(sortCol, current.dir);
        else applyFilters();
        if (cap) {
            while (shownTrs.length < Math.min(keepShown, matchedRows.length)) appendNextPage();
            rearmSentinel();
        }
        table.dataset.ftPltPriced = String(priced.size);
        table.dataset.ftPlt = 'done';
    }

    // ── Rune mounts: VISIBLE, so build them synchronously (no pop-in) ─────────────────────────────
    // The L1 rune glyph in each name cell (spells/curses/mentas/sanguines) is on-screen, so deferring
    // it made the runes visibly "pop in" a beat after the table appeared — which read as unpolished.
    // Keep it on the blocking path so the table only appears once it's complete. The footer's
    // DOMContentLoaded pass already ran before this async build; re-invoke the idempotent scanner
    // scoped to this table (mirrors the list-display / object-card bundles).
    if (typeof window.ftMountRuneIcons === 'function') {
        try { window.ftMountRuneIcons(container); } catch { /* footer pass covers init */ }
    }
    // Hydrate icon_ph placeholders the row HTML carries (e.g. my_player_characters / my_encounters /
    // feats action-button icons) synchronously here — they're visible, so avoid the deferred pop-in.
    // Idempotent with the initTooltips() pass below.
    if (typeof window.ftMountIcons === 'function') {
        try { window.ftMountIcons(container); } catch { /* footer pass covers init */ }
    }
    // Hydrate any [table:]/[random_table:] mounts inside a parsetext description cell.
    try { ftMountParseTextTables(container); } catch { /* footer pass covers init */ }
    window.ftMountEncounterDisplays?.(table);

    // ── Tooltips: INVISIBLE until hover, so defer off the critical path ───────────────────────────
    // initTooltips() only creates Bootstrap Tooltip instances; nothing shows until the user hovers an
    // icon. So creating them after first paint has zero visual effect (no pop-in) while saving the
    // tooltip pass (~640ms on a ~2k-row table) off the blocking build. (iconMap() emits
    // data-bs-toggle="tooltip" spans whose label is in data-bs-original-title — see initTooltips().)
    if (typeof window.requestAnimationFrame === 'function') {
        requestAnimationFrame(() => requestAnimationFrame(() => initTooltips(table)));
    } else {
        setTimeout(() => initTooltips(table), 0);
    }
}
